import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import {
  UNDEFINED_CHART_FEATURES,
  UNDEFINED_LOCKS,
  UNDEFINED_MPC_LOCKS
} from "entities/charts/chart";
import {
  DEFAULT_AVERAGE_FEATURE_ENTITY,
  DEFAULT_BEST_FIT_FEATURE_ENTITY,
  DEFAULT_CALCULATION_METHOD_FEATURE_ENTITY,
  DEFAULT_CHART_FOCUS_ENTITY,
  DEFAULT_CHART_TYPE_PARAM_FEATURE,
  DEFAULT_CUT_OFF_FEATURE_ENTITY,
  DEFAULT_DATA_TABLE_FEATURE_ENTITY,
  DEFAULT_FEATURE_ENTITY,
  DEFAULT_HIGHLIGHTED_LINE_OPACITY_FEATURE_ENTITY,
  DEFAULT_HIGHLIGHTED_LINE_SIZE_FEATURE_ENTITY,
  DEFAULT_HISTOGRAM_TOGGLE_FEATURE_ENTITY,
  DEFAULT_JITTER_PLOT_FEATURE_ENTITY,
  DEFAULT_LASSO_FEATURE_ENTITY,
  DEFAULT_LINE_OPACITY_FEATURE_ENTITY,
  DEFAULT_LINE_SIZE_FEATURE_ENTITY,
  DEFAULT_LOG_AXIS_FEATURE_ENTITY,
  DEFAULT_MOVING_AVERAGE_DAYS_FEATURE_ENTITY,
  DEFAULT_MULTI_PRODUCT_ENTITY,
  DEFAULT_MULTI_PRODUCT_FOCUS_ENTITY,
  DEFAULT_NORMALIZATION_ENTITY,
  DEFAULT_P10_FEATURE_ENTITY,
  DEFAULT_P50_FEATURE_ENTITY,
  DEFAULT_P90_FEATURE_ENTITY,
  DEFAULT_POINT_OPACITY_FEATURE_ENTITY,
  DEFAULT_POINT_SIZE_FEATURE_ENTITY,
  DEFAULT_PRESSURE_CHART_SETTINGS_ENTITY,
  DEFAULT_SCREENSHOT_FEATURE_ENTITY,
  DEFAULT_SHUT_IN_MONTHS_FEATURE_ENTITY,
  DEFAULT_SLOPE_LINE_FEATURE_ENTITY,
  DEFAULT_SOURCE_FEATURE_ENTITY,
  DEFAULT_TIME_STEP_FEATURE_ENTITY,
  DEFAULT_X_AXIS_SORT_ENTITY
} from "entities/charts/eva/features";
import _has from "lodash/has";
import _set from "lodash/set";
import { ChartEntity } from "types";
import { LockState } from "types/charts/locks";

import { DataEnum, Format } from "models";

export interface ChartSliceState {
  forecastZoomModeActive: boolean;
  resetForecastZoomMode: boolean;
  chart: Record<string, ChartEntity>;
}

const initialState: ChartSliceState = {
  forecastZoomModeActive: false,
  resetForecastZoomMode: false,
  chart: {}
};

export const chartSlice = createSlice({
  name: "charts",
  initialState,
  reducers: (create) => ({
    createChart: create.reducer((state, action: PayloadAction<Partial<ChartEntity>>) => {
      if (!state.chart[action.payload.id]) {
        Object.assign(state.chart, { [action.payload.id]: action.payload });
      }
    }),
    updateChart: create.reducer((state, action: PayloadAction<Partial<ChartEntity>>) => {
      if (state.chart[action.payload.id]) {
        Object.assign(state.chart[action.payload.id], action.payload);
      }
    }),
    removeChart: create.reducer((state, action: PayloadAction<string>) => {
      delete state.chart[action.payload];
    }),
    reset: create.reducer(() => initialState),
    updateChartByPath: create.reducer(
      (state, action: PayloadAction<{ id: string; path: string; value: Primitive }>) => {
        const chart = state.chart[action.payload.id];
        if (!chart) return;

        const { path, value } = action.payload;

        // mpcLocks paths are written with a structured direct assignment to avoid
        // prototype-pollution risk from lodash._set with server-supplied product keys.
        // Accepted format: mpcLocks.(x|y).<key>.(min|max)
        if (path.startsWith("mpcLocks.")) {
          const parts = path.split(".");
          const FORBIDDEN_KEYS = new Set(["__proto__", "constructor", "prototype"]);
          if (
            parts.length === 4 &&
            (parts[1] === "x" || parts[1] === "y") &&
            (parts[3] === "min" || parts[3] === "max") &&
            !FORBIDDEN_KEYS.has(parts[2])
          ) {
            const axis = parts[1] as "x" | "y";
            const key = parts[2];
            const field = parts[3] as "min" | "max";
            if (!chart.mpcLocks) chart.mpcLocks = { x: {}, y: {} };
            if (!chart.mpcLocks[axis][key]) {
              const empty = {
                value: undefined,
                type: DataEnum.None,
                format: Format.None
              };
              chart.mpcLocks[axis][key] = { min: empty, max: empty };
            }
            chart.mpcLocks[axis][key][field] = value as unknown as LockState;
          }
          return;
        }

        if (_has(chart, path)) {
          _set(chart, path, value);
        }
      }
    ),
    updateChartSize: create.reducer(
      (state, action: PayloadAction<{ id: string; width: number; height: number }>) => {
        const chart = state.chart[action.payload.id];
        if (chart) {
          chart.screenshot.size = {
            width: action.payload.width,
            height: action.payload.height
          };
        }
      }
    ),
    setForecastZoomModeActive: create.reducer((state, action: PayloadAction<boolean>) => {
      state.forecastZoomModeActive = action.payload;
    }),
    resetForecastZoomMode: create.reducer((state, action: PayloadAction<boolean>) => {
      state.resetForecastZoomMode = action.payload;
    })
  }),
  selectors: {
    selectChart: (state: ChartSliceState, id: string) => state.chart[id],
    selectAllCharts: (state: ChartSliceState) => state.chart,
    selectChartOptions: (state: ChartSliceState, id: string) =>
      state.chart[id]?.options ?? "{}",
    selectChartType: (state: ChartSliceState, id: string) =>
      state.chart[id]?.chartType ?? undefined,
    selectChartProduct: (state: ChartSliceState, id: string) =>
      state.chart[id]?.product ?? undefined,
    selectChartFeatures: (state: ChartSliceState, id: string) =>
      state.chart[id]?.features ?? UNDEFINED_CHART_FEATURES,
    selectChartResponse: (state: ChartSliceState, id: string) =>
      state.chart[id]?.response ?? undefined,
    selectChartRequest: (state: ChartSliceState, id: string) =>
      state.chart[id]?.request ?? undefined,
    selectChartBounds: (state: ChartSliceState, id: string) =>
      state.chart[id]?.bounds ?? undefined,
    selectChartOverflowIndex: (state: ChartSliceState, id: string) =>
      state.chart[id]?.overflowIndex ?? undefined,
    selectChartContextMenu: (state: ChartSliceState, id: string) =>
      state.chart[id]?.contextMenu ?? null,
    selectChartByPath: (state: ChartSliceState, id: string, path: string) => {
      const chart = state.chart[id];
      if (!chart) return undefined;
      const keys = path.split(".");

      let current = chart;
      for (const key of keys) {
        if (!current || !(key in current)) {
          return undefined;
        }
        current = current[key] as ChartEntity;
      }

      return current;
    },
    selectOverrideSizeLimit: (state: ChartSliceState, id: string) =>
      state.chart[id]?.overrideSizeLimit ?? false,
    selectChartSpaghettiFeature: (state: ChartSliceState, id: string) =>
      state.chart[id]?.features?.spaghetti ?? DEFAULT_FEATURE_ENTITY,
    selectChartDebugFeature: (state: ChartSliceState, id: string) =>
      state.chart[id]?.features?.debug ?? DEFAULT_FEATURE_ENTITY,
    selectChartForecastFeature: (state: ChartSliceState, id: string) =>
      state.chart[id]?.features?.forecast ?? DEFAULT_FEATURE_ENTITY,
    selectChartSourceFeature: (state: ChartSliceState, id: string) =>
      state.chart[id]?.features?.source ?? DEFAULT_SOURCE_FEATURE_ENTITY,
    selectChartIsProducingRateTypeFeature: (state: ChartSliceState, id: string) =>
      state.chart[id]?.features?.isProducingRateType ?? DEFAULT_SOURCE_FEATURE_ENTITY,
    selectChartTimeStepFeature: (state: ChartSliceState, id: string) =>
      state.chart[id]?.features?.timeStep ?? DEFAULT_TIME_STEP_FEATURE_ENTITY,
    selectChartDataTableFeature: (state: ChartSliceState, id: string) =>
      state.chart[id]?.features?.datatable ?? DEFAULT_DATA_TABLE_FEATURE_ENTITY,
    selectChartCutoffFeature: (state: ChartSliceState, id: string) =>
      state.chart[id]?.features?.cutoff ?? DEFAULT_CUT_OFF_FEATURE_ENTITY,
    selectChartShutInMonthsFeature: (state: ChartSliceState, id: string) =>
      state.chart[id]?.features?.shutInMonths ?? DEFAULT_SHUT_IN_MONTHS_FEATURE_ENTITY,
    selectChartMovingAverageDaysFeature: (state: ChartSliceState, id: string) =>
      state.chart[id]?.features?.movingAverageDays ??
      DEFAULT_MOVING_AVERAGE_DAYS_FEATURE_ENTITY,
    selectChartPointOpacityFeature: (state: ChartSliceState, id: string) =>
      state.chart[id]?.features?.pointOpacity ?? DEFAULT_POINT_OPACITY_FEATURE_ENTITY,
    selectChartPointSizeFeature: (state: ChartSliceState, id: string) =>
      state.chart[id]?.features?.pointSize ?? DEFAULT_POINT_SIZE_FEATURE_ENTITY,
    selectChartLineSizeFeature: (state: ChartSliceState, id: string) =>
      state.chart[id]?.features?.lineSize ?? DEFAULT_LINE_SIZE_FEATURE_ENTITY,
    selectChartLineOpacityFeature: (state: ChartSliceState, id: string) =>
      state.chart[id]?.features?.lineOpacity ?? DEFAULT_LINE_OPACITY_FEATURE_ENTITY,
    selectChartHighlightedLineSizeFeature: (state: ChartSliceState, id: string) =>
      state.chart[id]?.features?.highlightedLineSize ??
      DEFAULT_HIGHLIGHTED_LINE_SIZE_FEATURE_ENTITY,
    selectChartHighlightedLineOpacityFeature: (state: ChartSliceState, id: string) =>
      state.chart[id]?.features?.highlightedLineOpacity ??
      DEFAULT_HIGHLIGHTED_LINE_OPACITY_FEATURE_ENTITY,
    selectChartUseSeriesStyleOverrideFeature: (state: ChartSliceState, id: string) =>
      state.chart[id]?.features?.useSeriesStyleOverride ?? DEFAULT_FEATURE_ENTITY,
    selectChartSurvivorBiasFeature: (state: ChartSliceState, id: string) =>
      state.chart[id]?.features?.survivorBias ?? DEFAULT_FEATURE_ENTITY,
    selectChartUseWeightedRatioAverageFeature: (state: ChartSliceState, id: string) =>
      state.chart[id]?.features?.useWeightedRatioAverage ?? DEFAULT_FEATURE_ENTITY,
    selectChartLockUnitsFeature: (state: ChartSliceState, id: string) =>
      state.chart[id]?.features?.lockUnits ?? DEFAULT_FEATURE_ENTITY,
    selectChartLassoFeature: (state: ChartSliceState, id: string) =>
      state.chart[id]?.features?.lasso ?? DEFAULT_LASSO_FEATURE_ENTITY,
    selectChartLassoList: (state: ChartSliceState, id: string) =>
      (state.chart[id]?.features?.lasso?.properties?.list as string[]) ?? [],
    selectChartLassoBrushEnd: (state: ChartSliceState, id: string) =>
      (state.chart[id]?.features?.lasso?.properties?.brushEnd ?? false) as boolean,
    selectChartLassoBrushPosition: (state: ChartSliceState, id: string) =>
      state.chart[id]?.features?.lasso?.properties?.position as {
        x: number;
        y: number;
      },
    selectChartTypeWellsFeature: (state: ChartSliceState, id: string) =>
      state.chart[id]?.features?.typewells ?? DEFAULT_FEATURE_ENTITY,
    selectChartWorkingForecastFeature: (state: ChartSliceState, id: string) =>
      state.chart[id]?.features?.workingForecast ?? DEFAULT_FEATURE_ENTITY,
    selectChartP10Feature: (state: ChartSliceState, id: string) =>
      state.chart[id]?.features?.p10 ?? DEFAULT_P10_FEATURE_ENTITY,
    selectChartP50Feature: (state: ChartSliceState, id: string) =>
      state.chart[id]?.features?.p50 ?? DEFAULT_P50_FEATURE_ENTITY,
    selectChartP90Feature: (state: ChartSliceState, id: string) =>
      state.chart[id]?.features?.p90 ?? DEFAULT_P90_FEATURE_ENTITY,
    selectChartAverageFeature: (state: ChartSliceState, id: string) =>
      state.chart[id]?.features?.average ?? DEFAULT_AVERAGE_FEATURE_ENTITY,
    selectChartScreenshotFeature: (state: ChartSliceState, id: string) =>
      state.chart[id]?.features?.screenshot ?? DEFAULT_SCREENSHOT_FEATURE_ENTITY,
    selectChartFullScreenFeature: (state: ChartSliceState, id: string) =>
      state.chart[id]?.features?.fullScreen ?? DEFAULT_FEATURE_ENTITY,
    selectChartNormalizationFeature: (state: ChartSliceState, id: string) =>
      state.chart[id]?.features?.normalization ?? DEFAULT_NORMALIZATION_ENTITY,
    selectChartLocks: (state: ChartSliceState, id: string) =>
      state.chart[id]?.locks ?? UNDEFINED_LOCKS,
    selectChartMpcLocks: (state: ChartSliceState, id: string) =>
      state.chart[id]?.mpcLocks ?? UNDEFINED_MPC_LOCKS,
    selectChartFocusFeature: (state: ChartSliceState, id: string) =>
      state.chart[id]?.features?.chartFocus ?? DEFAULT_CHART_FOCUS_ENTITY,
    selectChartMultiProductFeature: (state: ChartSliceState, id: string) =>
      state.chart[id]?.features?.multiProduct ?? DEFAULT_MULTI_PRODUCT_ENTITY,
    selectChartMultiProductFocusFeature: (state: ChartSliceState, id: string) =>
      state.chart[id]?.features?.multiProductFocus ?? DEFAULT_MULTI_PRODUCT_FOCUS_ENTITY,
    selectChartWellCountFeature: (state: ChartSliceState, id: string) =>
      state.chart[id]?.features?.showWellCount ?? DEFAULT_FEATURE_ENTITY,
    selectChartAnnotationFeature: (state: ChartSliceState, id: string) =>
      state.chart[id]?.features?.annotations ?? DEFAULT_FEATURE_ENTITY,
    selectChartLegendFeature: (state: ChartSliceState, id: string) =>
      state.chart[id]?.features?.legend ?? DEFAULT_FEATURE_ENTITY,
    selectChartTypeParamFeature: (state: ChartSliceState, id: string) =>
      state.chart[id]?.features?.chartTypeParam ?? DEFAULT_CHART_TYPE_PARAM_FEATURE,
    selectPressureChartSettingsFeature: (state: ChartSliceState, id: string) =>
      state.chart[id]?.features?.pressureChartSettings ??
      DEFAULT_PRESSURE_CHART_SETTINGS_ENTITY,
    selectChartHistogramToggleFeature: (state: ChartSliceState, id: string) =>
      state.chart[id]?.features?.histogramToggle ??
      DEFAULT_HISTOGRAM_TOGGLE_FEATURE_ENTITY,
    selectChartJitterPlotToggleFeature: (state: ChartSliceState, id: string) =>
      state.chart[id]?.features?.jitterPlotToggle ?? DEFAULT_JITTER_PLOT_FEATURE_ENTITY,
    selectBestFitToggleFeature: (state: ChartSliceState, id: string) =>
      state.chart[id]?.features?.bestFitToggle ?? DEFAULT_BEST_FIT_FEATURE_ENTITY,
    selectChartCalculationMethodFeature: (state: ChartSliceState, id: string) =>
      state.chart[id]?.features?.calculationMethod ??
      DEFAULT_CALCULATION_METHOD_FEATURE_ENTITY,
    selectLogAxisFeature: (state: ChartSliceState, id: string) =>
      state.chart[id]?.features?.logAxis ?? DEFAULT_LOG_AXIS_FEATURE_ENTITY,
    selectUseProbitLineOfBestFitData: (state: ChartSliceState, id: string) =>
      state.chart[id]?.features?.useProbitLineOfBestFitData ?? DEFAULT_FEATURE_ENTITY,
    selectUseLineOfBestFit: (state: ChartSliceState, id: string) =>
      state.chart[id]?.features?.useLineOfBestFit ?? DEFAULT_FEATURE_ENTITY,
    selectShowZeros: (state: ChartSliceState, id: string) =>
      state.chart[id]?.features?.showZeros ?? DEFAULT_FEATURE_ENTITY,
    selectQuarterSlopeFeature: (state: ChartSliceState, id: string) =>
      state.chart[id]?.features?.quarterSlope ?? DEFAULT_SLOPE_LINE_FEATURE_ENTITY,
    selectHalfSlopeFeature: (state: ChartSliceState, id: string) =>
      state.chart[id]?.features?.halfSlope ?? DEFAULT_SLOPE_LINE_FEATURE_ENTITY,
    selectUnitSlopeFeature: (state: ChartSliceState, id: string) =>
      state.chart[id]?.features?.unitSlope ?? DEFAULT_SLOPE_LINE_FEATURE_ENTITY,
    selectChartHighlight: (state: ChartSliceState, id: string) =>
      state.chart[id]?.highlight ?? true,
    selectForecastZoomMode: (state: ChartSliceState) => state.forecastZoomModeActive,
    selectChartLicensedVolumeLineFeature: (state: ChartSliceState, id: string) =>
      state.chart[id]?.features?.licensedVolumeLine ?? DEFAULT_FEATURE_ENTITY,
    selectChartXAxisFieldSyncFeature: (state: ChartSliceState, id: string) =>
      state.chart[id]?.features?.xAxisFieldSync ?? DEFAULT_FEATURE_ENTITY,
    selectChartXAxisSortFeature: (state: ChartSliceState, id: string) =>
      state.chart[id]?.features?.xAxisSort ?? DEFAULT_X_AXIS_SORT_ENTITY
  }
});

export const {
  createChart,
  updateChart,
  removeChart,
  updateChartByPath,
  updateChartSize,
  reset,
  setForecastZoomModeActive,
  resetForecastZoomMode
} = chartSlice.actions;
export const {
  selectChartOptions,
  selectChartType,
  selectChartProduct,
  selectChartResponse,
  selectChartRequest,
  selectChartBounds,
  selectChartOverflowIndex,
  selectChartContextMenu,
  selectChart,
  selectChartFeatures,
  selectChartByPath,
  selectOverrideSizeLimit,
  selectChartDebugFeature,
  selectChartForecastFeature,
  selectChartSourceFeature,
  selectChartIsProducingRateTypeFeature,
  selectChartTimeStepFeature,
  selectChartCutoffFeature,
  selectChartShutInMonthsFeature,
  selectChartMovingAverageDaysFeature,
  selectChartPointOpacityFeature,
  selectChartPointSizeFeature,
  selectChartLineSizeFeature,
  selectChartLineOpacityFeature,
  selectChartHighlightedLineSizeFeature,
  selectChartHighlightedLineOpacityFeature,
  selectChartUseSeriesStyleOverrideFeature,
  selectChartSurvivorBiasFeature,
  selectChartUseWeightedRatioAverageFeature,
  selectChartLockUnitsFeature,
  selectChartSpaghettiFeature,
  selectChartDataTableFeature,
  selectChartLassoFeature,
  selectChartLassoList,
  selectChartLassoBrushEnd,
  selectChartLassoBrushPosition,
  selectChartTypeWellsFeature,
  selectChartWorkingForecastFeature,
  selectChartP10Feature,
  selectChartP50Feature,
  selectChartP90Feature,
  selectChartAverageFeature,
  selectChartScreenshotFeature,
  selectChartFullScreenFeature,
  selectChartNormalizationFeature,
  selectChartLocks,
  selectChartFocusFeature,
  selectChartMultiProductFeature,
  selectChartMultiProductFocusFeature,
  selectChartWellCountFeature,
  selectChartAnnotationFeature,
  selectChartLegendFeature,
  selectChartTypeParamFeature,
  selectChartHistogramToggleFeature,
  selectChartJitterPlotToggleFeature,
  selectBestFitToggleFeature,
  selectPressureChartSettingsFeature,
  selectChartCalculationMethodFeature,
  selectLogAxisFeature,
  selectUseProbitLineOfBestFitData,
  selectUseLineOfBestFit,
  selectShowZeros,
  selectHalfSlopeFeature,
  selectQuarterSlopeFeature,
  selectUnitSlopeFeature,
  selectChartHighlight,
  selectAllCharts,
  selectForecastZoomMode,
  selectChartLicensedVolumeLineFeature,
  selectChartXAxisFieldSyncFeature,
  selectChartXAxisSortFeature,
  selectChartMpcLocks
} = chartSlice.selectors;

export default chartSlice.reducer;
